import {
	createActionsHook,
	createHook,
	createStore,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

import type {
	AppliedFieldsChanges,
	Field,
	FieldId,
	FieldInternalShape,
	FieldInternalValue,
	FieldMapFromIframe,
	FieldType,
} from '../../common/types/field';

import { addAppsErrors, resetAppErrors, setAppErrors } from './actions/app-errors';
import { detectAppliedChangesConflicts } from './actions/applied-changes-history';
import {
	collectFieldsAppliedChangesHistory,
	collectScreenTabsAppliedChangesHistory,
} from './actions/applied-changes-history/collect-history';
import { resetError, setError } from './actions/errors';
import {
	startFieldsInitialisationTimeout,
	successFieldsInitalisation,
} from './actions/fields-initialisation-timeout';
import {
	initializeField,
	initializeFields,
	type InitializeFieldPayload,
} from './actions/initialize-field';
import { initializeNoScreenTab } from './actions/initialize-no-screen-tab';
import { initializeScreenTabs } from './actions/initialize-screen-tabs';
import { resetNotification, setNotification } from './actions/notification';
import { processChange, type ProcessChangePayload } from './actions/process-change';
import { registerFields, unregisterFields } from './actions/register-fields';
import { setFieldValue } from './actions/set-field-value';
import { updateActiveScreenTab } from './actions/update-active-screen-tab';
import { updateFields } from './actions/update-fields';
import { updateScreenTabs } from './actions/update-screen-tabs';
// eslint-disable-next-line import/order
import type {
	AppId,
	ChangeId,
	IssueAdjustmentsState,
	RegisteredFields,
	StoreContainerProps,
} from './types';
// eslint-disable-next-line import/order
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
// eslint-disable-next-line import/order
import type { IntlShape } from 'react-intl';
// eslint-disable-next-line import/order
import type { IEnvironment } from 'relay-runtime';
// eslint-disable-next-line import/order
import type { ScreenTabsDataPublic } from '../../common/types/screen-tabs';
// eslint-disable-next-line import/order
import type {
	LookupData,
	TenantContext,
	ExecutionContext,
	ScreenTabsChangesPublic,
	FieldChangesMapPublic,
	FieldOptionsFilter,
	AllowedValuesOverrides,
} from '../../types';
// eslint-disable-next-line import/order
import type { ErrorState } from './actions/errors/types';
// eslint-disable-next-line import/order
import type { NotificationState } from './actions/notification/types';
import type { AppError } from './utils/errors/types';

const name = 'issue-adjustments';
export const initialState: IssueAdjustmentsState = {
	formData: null,
	screenTabs: null,
	appliedChanges: null,
	error: null,
	registeredFieldsDeprecated: {},
	registeredFields: {},
	triggerFieldInitializationKey: 0,
	// Selector checks if this is not equal 0 in the useFieldReady
	numberOfSupportedFields: 0,
	internalFormMetadata: {},
	viewType: null,
	experienceDataIsLoading: false,
	lastProcessedChange: null,
	fieldsInitialization: {
		startTime: null,
		timeoutId: undefined,
		status: 'notStarted',
	},
	appliedChangesHistory: {},
	appsErrors: {},
	notification: null,
};

// triggerFieldInitializationKey, viewType and numberOfSupportedFields should not be reset on project or issue type change
const {
	triggerFieldInitializationKey,
	viewType: scopedViewType,
	numberOfSupportedFields,
	...stateForResetAllFields
} = initialState;

export const actions: {
	triggerFieldInitialization: () => ({
		getState,
		setState,
	}: StoreActionApi<IssueAdjustmentsState>) => void;
	processChange: ({
		fieldId,
		fieldValue,
		eventType,
	}: Omit<ProcessChangePayload, 'fieldType'>) => (
		{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	) => void;
	setFieldValue: ({
		fieldId,
		fieldType,
		fieldValue,
	}: {
		fieldId: FieldId;
		fieldType: FieldType;
		fieldValue: FieldInternalValue;
	}) => (
		{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	) => void;
	initializeField: ({
		field,
		metadata,
	}: InitializeFieldPayload) => (
		{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	) => void;
	initializeFields: (
		fields: InitializeFieldPayload[],
	) => (
		{ setState }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	) => void;
	startFieldsInitialisationTimeout: ({
		createAnalyticsEvent,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
	}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
	successFieldsInitalisation: ({
		createAnalyticsEvent,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
	}) => (
		{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType }: StoreContainerProps,
	) => void;
	registerFields: ({
		changeId,
		fieldsToBeRegistered,
		appId,
	}: {
		changeId: ChangeId;
		fieldsToBeRegistered: RegisteredFields;
		appId: AppId;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	unregisterFields: ({
		changeId,
		appId,
	}: {
		changeId: ChangeId;
		appId: AppId;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	updateFields: ({
		changeId,
		fieldsChanges,
		lookupDataFromApi,
		tenantContext,
		executionContext,
		environment,
		appId,
		intl,
		createAnalyticsEvent,
	}: {
		changeId: ChangeId;
		fieldsChanges: FieldMapFromIframe;
		lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
		tenantContext: TenantContext;
		executionContext: ExecutionContext;
		environment: IEnvironment;
		appId: AppId;
		intl: IntlShape;
		createAnalyticsEvent: CreateUIAnalyticsEvent;
	}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
	clearAppliedValue: (
		fieldId: FieldId,
	) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	setError: (
		errorProperties: ErrorState,
	) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	setNumberOfSupportedFields: (
		numberOfAllSupportedFields: number,
	) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	/**
	 * This is used to block onInit for specific case like Issue View background refresh loading.
	 */
	setExperienceDataIsLoading: (
		experienceDataIsLoading: boolean,
	) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	setAppErrors: ({
		errors,
		appId,
	}: {
		errors: AppError[];
		appId: AppId;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	resetAppErrors: ({
		appId,
	}: {
		appId: AppId;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	addAppsErrors: ({
		errors,
	}: {
		errors: Record<AppId, AppError[]>;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	initializeScreenTabs: ({
		tabs,
		selected,
	}: {
		tabs: Readonly<
			Array<{
				id: string;
			}>
		>;
		selected?: number;
	}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	updateScreenTabs: ({
		screenTabsChanges,
		changeId,
		appId,
	}: {
		screenTabsChanges: ScreenTabsChangesPublic;
		appId: AppId;
		changeId: ChangeId;
	}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
	updateActiveScreenTab: ({
		selected,
	}: {
		selected: number;
	}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	detectAppliedChangesConflicts: ({
		appId,
		changeId,
	}: {
		appId: AppId;
		changeId: ChangeId;
	}) => (
		{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType, createAnalyticsEvent }: StoreContainerProps,
	) => void;
	collectScreenTabsAppliedChangesHistory: ({
		appId,
		changeId,
		screenTabs,
	}: {
		appId: AppId;
		changeId: ChangeId;
		screenTabs: ScreenTabsDataPublic;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	collectFieldsAppliedChangesHistory: ({
		appId,
		changeId,
		fieldsChanges,
	}: {
		appId: AppId;
		changeId: ChangeId;
		fieldsChanges: FieldChangesMapPublic;
	}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
	setNotification: (
		notificationProperties: NotificationState,
	) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
} = {
	triggerFieldInitialization:
		() =>
		({ getState, setState }: StoreActionApi<IssueAdjustmentsState>): void => {
			setState({
				triggerFieldInitializationKey: getState().triggerFieldInitializationKey + 1,
			});
		},
	processChange,
	setFieldValue,
	initializeField,
	initializeFields,
	startFieldsInitialisationTimeout,
	successFieldsInitalisation,
	registerFields,
	unregisterFields,
	updateFields,
	clearAppliedValue:
		(fieldId: FieldId) =>
		({ setState, getState }: StoreActionApi<IssueAdjustmentsState>): void => {
			const previousState = getState().appliedChanges || {};

			if (previousState[fieldId]) {
				const { value, ...otherFieldChanges } = previousState[fieldId];

				setState({
					appliedChanges: {
						...previousState,
						[fieldId]: otherFieldChanges,
					},
				});
			}
		},
	resetAllFields:
		() =>
		({ setState, getState }: StoreActionApi<IssueAdjustmentsState>): void => {
			// Clear the fieldsInitialization timeout to avoid potetianl side effects.
			clearTimeout(getState().fieldsInitialization.timeoutId);

			setState(stateForResetAllFields);
		},
	setError,
	resetError,
	setNumberOfSupportedFields:
		(numberOfAllSupportedFields: number) =>
		({ setState }: StoreActionApi<IssueAdjustmentsState>): void => {
			setState({ numberOfSupportedFields: numberOfAllSupportedFields });
		},
	/**
	 * This is used to block onInit for specific case like Issue View background refresh loading.
	 */
	setExperienceDataIsLoading:
		(experienceDataIsLoading: boolean) =>
		({ setState }: StoreActionApi<IssueAdjustmentsState>): void => {
			setState({ experienceDataIsLoading });
		},
	setAppErrors,
	resetAppErrors,
	addAppsErrors,
	initializeNoScreenTab,
	initializeScreenTabs,
	updateScreenTabs,
	updateActiveScreenTab,
	detectAppliedChangesConflicts,
	collectScreenTabsAppliedChangesHistory,
	collectFieldsAppliedChangesHistory,
	setNotification,
	resetNotification,
};

export type Actions = typeof actions;
// It was required to satisfiy TS with the async action
export type HookActions = ReturnType<typeof useIssueAdjustmentsActions>;

export const store: Store<
	IssueAdjustmentsState,
	{
		triggerFieldInitialization: () => ({
			getState,
			setState,
		}: StoreActionApi<IssueAdjustmentsState>) => void;
		processChange: ({
			fieldId,
			fieldValue,
			eventType,
		}: Omit<ProcessChangePayload, 'fieldType'>) => (
			{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
			{ viewType }: StoreContainerProps,
		) => void;
		setFieldValue: ({
			fieldId,
			fieldType,
			fieldValue,
		}: {
			fieldId: FieldId;
			fieldType: FieldType;
			fieldValue: FieldInternalValue;
		}) => (
			{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
			{ viewType }: StoreContainerProps,
		) => void;
		initializeField: ({
			field,
			metadata,
		}: InitializeFieldPayload) => (
			{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
			{ viewType }: StoreContainerProps,
		) => void;
		initializeFields: (
			fields: InitializeFieldPayload[],
		) => (
			{ setState }: StoreActionApi<IssueAdjustmentsState>,
			{ viewType }: StoreContainerProps,
		) => void;
		startFieldsInitialisationTimeout: ({
			createAnalyticsEvent,
		}: {
			createAnalyticsEvent: CreateUIAnalyticsEvent;
		}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
		successFieldsInitalisation: ({
			createAnalyticsEvent,
		}: {
			createAnalyticsEvent: CreateUIAnalyticsEvent;
		}) => (
			{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
			{ viewType }: StoreContainerProps,
		) => void;
		registerFields: ({
			changeId,
			fieldsToBeRegistered,
			appId,
		}: {
			changeId: ChangeId;
			fieldsToBeRegistered: RegisteredFields;
			appId: AppId;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		unregisterFields: ({
			changeId,
			appId,
		}: {
			changeId: ChangeId;
			appId: AppId;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		updateFields: ({
			changeId,
			fieldsChanges,
			lookupDataFromApi,
			tenantContext,
			executionContext,
			environment,
			appId,
			intl,
			createAnalyticsEvent,
		}: {
			changeId: ChangeId;
			fieldsChanges: FieldMapFromIframe;
			lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
			tenantContext: TenantContext;
			executionContext: ExecutionContext;
			environment: IEnvironment;
			appId: AppId;
			intl: IntlShape;
			createAnalyticsEvent: CreateUIAnalyticsEvent;
		}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
		clearAppliedValue: (
			fieldId: FieldId,
		) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		setError: (
			errorProperties: ErrorState,
		) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		setNumberOfSupportedFields: (
			numberOfAllSupportedFields: number,
		) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		/**
		 * This is used to block onInit for specific case like Issue View background refresh loading.
		 */
		setExperienceDataIsLoading: (
			experienceDataIsLoading: boolean,
		) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		setAppErrors: ({
			errors,
			appId,
		}: {
			errors: AppError[];
			appId: AppId;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		resetAppErrors: ({
			appId,
		}: {
			appId: AppId;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		addAppsErrors: ({
			errors,
		}: {
			errors: Record<AppId, AppError[]>;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		initializeScreenTabs: ({
			tabs,
			selected,
		}: {
			tabs: Readonly<
				Array<{
					id: string;
				}>
			>;
			selected?: number;
		}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		updateScreenTabs: ({
			screenTabsChanges,
			changeId,
			appId,
		}: {
			screenTabsChanges: ScreenTabsChangesPublic;
			appId: AppId;
			changeId: ChangeId;
		}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
		updateActiveScreenTab: ({
			selected,
		}: {
			selected: number;
		}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		detectAppliedChangesConflicts: ({
			appId,
			changeId,
		}: {
			appId: AppId;
			changeId: ChangeId;
		}) => (
			{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
			{ viewType, createAnalyticsEvent }: StoreContainerProps,
		) => void;
		collectScreenTabsAppliedChangesHistory: ({
			appId,
			changeId,
			screenTabs,
		}: {
			appId: AppId;
			changeId: ChangeId;
			screenTabs: ScreenTabsDataPublic;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		collectFieldsAppliedChangesHistory: ({
			appId,
			changeId,
			fieldsChanges,
		}: {
			appId: AppId;
			changeId: ChangeId;
			fieldsChanges: FieldChangesMapPublic;
		}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
		setNotification: (
			notificationProperties: NotificationState,
		) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
	}
> = createStore<IssueAdjustmentsState, Actions>({
	name,
	initialState,
	actions,
});

export const useIssueAdjustments: HookFunction<
	IssueAdjustmentsState,
	BoundActions<
		IssueAdjustmentsState,
		{
			triggerFieldInitialization: () => ({
				getState,
				setState,
			}: StoreActionApi<IssueAdjustmentsState>) => void;
			processChange: ({
				fieldId,
				fieldValue,
				eventType,
			}: Omit<ProcessChangePayload, 'fieldType'>) => (
				{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			setFieldValue: ({
				fieldId,
				fieldType,
				fieldValue,
			}: {
				fieldId: FieldId;
				fieldType: FieldType;
				fieldValue: FieldInternalValue;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeField: ({
				field,
				metadata,
			}: InitializeFieldPayload) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeFields: (
				fields: InitializeFieldPayload[],
			) => (
				{ setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			startFieldsInitialisationTimeout: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			successFieldsInitalisation: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			registerFields: ({
				changeId,
				fieldsToBeRegistered,
				appId,
			}: {
				changeId: ChangeId;
				fieldsToBeRegistered: RegisteredFields;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			unregisterFields: ({
				changeId,
				appId,
			}: {
				changeId: ChangeId;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateFields: ({
				changeId,
				fieldsChanges,
				lookupDataFromApi,
				tenantContext,
				executionContext,
				environment,
				appId,
				intl,
				createAnalyticsEvent,
			}: {
				changeId: ChangeId;
				fieldsChanges: FieldMapFromIframe;
				lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
				tenantContext: TenantContext;
				executionContext: ExecutionContext;
				environment: IEnvironment;
				appId: AppId;
				intl: IntlShape;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
			clearAppliedValue: (
				fieldId: FieldId,
			) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setError: (
				errorProperties: ErrorState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNumberOfSupportedFields: (
				numberOfAllSupportedFields: number,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			/**
			 * This is used to block onInit for specific case like Issue View background refresh loading.
			 */
			setExperienceDataIsLoading: (
				experienceDataIsLoading: boolean,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setAppErrors: ({
				errors,
				appId,
			}: {
				errors: AppError[];
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAppErrors: ({
				appId,
			}: {
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			addAppsErrors: ({
				errors,
			}: {
				errors: Record<AppId, AppError[]>;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeScreenTabs: ({
				tabs,
				selected,
			}: {
				tabs: Readonly<
					Array<{
						id: string;
					}>
				>;
				selected?: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateScreenTabs: ({
				screenTabsChanges,
				changeId,
				appId,
			}: {
				screenTabsChanges: ScreenTabsChangesPublic;
				appId: AppId;
				changeId: ChangeId;
			}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateActiveScreenTab: ({
				selected,
			}: {
				selected: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			detectAppliedChangesConflicts: ({
				appId,
				changeId,
			}: {
				appId: AppId;
				changeId: ChangeId;
			}) => (
				{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType, createAnalyticsEvent }: StoreContainerProps,
			) => void;
			collectScreenTabsAppliedChangesHistory: ({
				appId,
				changeId,
				screenTabs,
			}: {
				appId: AppId;
				changeId: ChangeId;
				screenTabs: ScreenTabsDataPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			collectFieldsAppliedChangesHistory: ({
				appId,
				changeId,
				fieldsChanges,
			}: {
				appId: AppId;
				changeId: ChangeId;
				fieldsChanges: FieldChangesMapPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNotification: (
				notificationProperties: NotificationState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		}
	>,
	void
> = createHook(store);

const stableEmptyFieldFromIframe: FieldInternalShape = {};

export const useAdjustedFields: HookFunction<
	AppliedFieldsChanges | null,
	BoundActions<
		IssueAdjustmentsState,
		{
			triggerFieldInitialization: () => ({
				getState,
				setState,
			}: StoreActionApi<IssueAdjustmentsState>) => void;
			processChange: ({
				fieldId,
				fieldValue,
				eventType,
			}: Omit<ProcessChangePayload, 'fieldType'>) => (
				{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			setFieldValue: ({
				fieldId,
				fieldType,
				fieldValue,
			}: {
				fieldId: FieldId;
				fieldType: FieldType;
				fieldValue: FieldInternalValue;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeField: ({
				field,
				metadata,
			}: InitializeFieldPayload) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeFields: (
				fields: InitializeFieldPayload[],
			) => (
				{ setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			startFieldsInitialisationTimeout: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			successFieldsInitalisation: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			registerFields: ({
				changeId,
				fieldsToBeRegistered,
				appId,
			}: {
				changeId: ChangeId;
				fieldsToBeRegistered: RegisteredFields;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			unregisterFields: ({
				changeId,
				appId,
			}: {
				changeId: ChangeId;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateFields: ({
				changeId,
				fieldsChanges,
				lookupDataFromApi,
				tenantContext,
				executionContext,
				environment,
				appId,
				intl,
				createAnalyticsEvent,
			}: {
				changeId: ChangeId;
				fieldsChanges: FieldMapFromIframe;
				lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
				tenantContext: TenantContext;
				executionContext: ExecutionContext;
				environment: IEnvironment;
				appId: AppId;
				intl: IntlShape;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
			clearAppliedValue: (
				fieldId: FieldId,
			) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setError: (
				errorProperties: ErrorState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNumberOfSupportedFields: (
				numberOfAllSupportedFields: number,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			/**
			 * This is used to block onInit for specific case like Issue View background refresh loading.
			 */
			setExperienceDataIsLoading: (
				experienceDataIsLoading: boolean,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setAppErrors: ({
				errors,
				appId,
			}: {
				errors: AppError[];
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAppErrors: ({
				appId,
			}: {
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			addAppsErrors: ({
				errors,
			}: {
				errors: Record<AppId, AppError[]>;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeScreenTabs: ({
				tabs,
				selected,
			}: {
				tabs: Readonly<
					Array<{
						id: string;
					}>
				>;
				selected?: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateScreenTabs: ({
				screenTabsChanges,
				changeId,
				appId,
			}: {
				screenTabsChanges: ScreenTabsChangesPublic;
				appId: AppId;
				changeId: ChangeId;
			}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateActiveScreenTab: ({
				selected,
			}: {
				selected: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			detectAppliedChangesConflicts: ({
				appId,
				changeId,
			}: {
				appId: AppId;
				changeId: ChangeId;
			}) => (
				{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType, createAnalyticsEvent }: StoreContainerProps,
			) => void;
			collectScreenTabsAppliedChangesHistory: ({
				appId,
				changeId,
				screenTabs,
			}: {
				appId: AppId;
				changeId: ChangeId;
				screenTabs: ScreenTabsDataPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			collectFieldsAppliedChangesHistory: ({
				appId,
				changeId,
				fieldsChanges,
			}: {
				appId: AppId;
				changeId: ChangeId;
				fieldsChanges: FieldChangesMapPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNotification: (
				notificationProperties: NotificationState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state: IssueAdjustmentsState) => state.appliedChanges,
});

export const useAdjustedField: HookFunction<
	Partial<{
		fieldName?: Field['fieldName'];
		description?: Field['description'];
		isVisible?: Field['isVisible'];
		isReadOnly?: boolean;
		isRequired?: boolean;
		value?: any;
		allowedValues?: unknown;
		fieldOptionsFilter?: FieldOptionsFilter;
		allowedValuesOverrides?: AllowedValuesOverrides;
		isSameFieldValueChanged?: boolean;
	}>,
	BoundActions<
		IssueAdjustmentsState,
		{
			triggerFieldInitialization: () => ({
				getState,
				setState,
			}: StoreActionApi<IssueAdjustmentsState>) => void;
			processChange: ({
				fieldId,
				fieldValue,
				eventType,
			}: Omit<ProcessChangePayload, 'fieldType'>) => (
				{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			setFieldValue: ({
				fieldId,
				fieldType,
				fieldValue,
			}: {
				fieldId: FieldId;
				fieldType: FieldType;
				fieldValue: FieldInternalValue;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeField: ({
				field,
				metadata,
			}: InitializeFieldPayload) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeFields: (
				fields: InitializeFieldPayload[],
			) => (
				{ setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			startFieldsInitialisationTimeout: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			successFieldsInitalisation: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			registerFields: ({
				changeId,
				fieldsToBeRegistered,
				appId,
			}: {
				changeId: ChangeId;
				fieldsToBeRegistered: RegisteredFields;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			unregisterFields: ({
				changeId,
				appId,
			}: {
				changeId: ChangeId;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateFields: ({
				changeId,
				fieldsChanges,
				lookupDataFromApi,
				tenantContext,
				executionContext,
				environment,
				appId,
				intl,
				createAnalyticsEvent,
			}: {
				changeId: ChangeId;
				fieldsChanges: FieldMapFromIframe;
				lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
				tenantContext: TenantContext;
				executionContext: ExecutionContext;
				environment: IEnvironment;
				appId: AppId;
				intl: IntlShape;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
			clearAppliedValue: (
				fieldId: FieldId,
			) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setError: (
				errorProperties: ErrorState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNumberOfSupportedFields: (
				numberOfAllSupportedFields: number,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			/**
			 * This is used to block onInit for specific case like Issue View background refresh loading.
			 */
			setExperienceDataIsLoading: (
				experienceDataIsLoading: boolean,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setAppErrors: ({
				errors,
				appId,
			}: {
				errors: AppError[];
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAppErrors: ({
				appId,
			}: {
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			addAppsErrors: ({
				errors,
			}: {
				errors: Record<AppId, AppError[]>;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeScreenTabs: ({
				tabs,
				selected,
			}: {
				tabs: Readonly<
					Array<{
						id: string;
					}>
				>;
				selected?: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateScreenTabs: ({
				screenTabsChanges,
				changeId,
				appId,
			}: {
				screenTabsChanges: ScreenTabsChangesPublic;
				appId: AppId;
				changeId: ChangeId;
			}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateActiveScreenTab: ({
				selected,
			}: {
				selected: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			detectAppliedChangesConflicts: ({
				appId,
				changeId,
			}: {
				appId: AppId;
				changeId: ChangeId;
			}) => (
				{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType, createAnalyticsEvent }: StoreContainerProps,
			) => void;
			collectScreenTabsAppliedChangesHistory: ({
				appId,
				changeId,
				screenTabs,
			}: {
				appId: AppId;
				changeId: ChangeId;
				screenTabs: ScreenTabsDataPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			collectFieldsAppliedChangesHistory: ({
				appId,
				changeId,
				fieldsChanges,
			}: {
				appId: AppId;
				changeId: ChangeId;
				fieldsChanges: FieldChangesMapPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNotification: (
				notificationProperties: NotificationState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		}
	>,
	string
> = createHook(store, {
	selector: (state: IssueAdjustmentsState, fieldId: FieldId) =>
		state.appliedChanges?.[fieldId] || stableEmptyFieldFromIframe,
});

export const useIssueAdjustmentsActions: HookActionsFunction<
	BoundActions<
		IssueAdjustmentsState,
		{
			triggerFieldInitialization: () => ({
				getState,
				setState,
			}: StoreActionApi<IssueAdjustmentsState>) => void;
			processChange: ({
				fieldId,
				fieldValue,
				eventType,
			}: Omit<ProcessChangePayload, 'fieldType'>) => (
				{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			setFieldValue: ({
				fieldId,
				fieldType,
				fieldValue,
			}: {
				fieldId: FieldId;
				fieldType: FieldType;
				fieldValue: FieldInternalValue;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeField: ({
				field,
				metadata,
			}: InitializeFieldPayload) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeFields: (
				fields: InitializeFieldPayload[],
			) => (
				{ setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			startFieldsInitialisationTimeout: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			successFieldsInitalisation: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			registerFields: ({
				changeId,
				fieldsToBeRegistered,
				appId,
			}: {
				changeId: ChangeId;
				fieldsToBeRegistered: RegisteredFields;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			unregisterFields: ({
				changeId,
				appId,
			}: {
				changeId: ChangeId;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateFields: ({
				changeId,
				fieldsChanges,
				lookupDataFromApi,
				tenantContext,
				executionContext,
				environment,
				appId,
				intl,
				createAnalyticsEvent,
			}: {
				changeId: ChangeId;
				fieldsChanges: FieldMapFromIframe;
				lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
				tenantContext: TenantContext;
				executionContext: ExecutionContext;
				environment: IEnvironment;
				appId: AppId;
				intl: IntlShape;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
			clearAppliedValue: (
				fieldId: FieldId,
			) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setError: (
				errorProperties: ErrorState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNumberOfSupportedFields: (
				numberOfAllSupportedFields: number,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			/**
			 * This is used to block onInit for specific case like Issue View background refresh loading.
			 */
			setExperienceDataIsLoading: (
				experienceDataIsLoading: boolean,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setAppErrors: ({
				errors,
				appId,
			}: {
				errors: AppError[];
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAppErrors: ({
				appId,
			}: {
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			addAppsErrors: ({
				errors,
			}: {
				errors: Record<AppId, AppError[]>;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeScreenTabs: ({
				tabs,
				selected,
			}: {
				tabs: Readonly<
					Array<{
						id: string;
					}>
				>;
				selected?: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateScreenTabs: ({
				screenTabsChanges,
				changeId,
				appId,
			}: {
				screenTabsChanges: ScreenTabsChangesPublic;
				appId: AppId;
				changeId: ChangeId;
			}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateActiveScreenTab: ({
				selected,
			}: {
				selected: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			detectAppliedChangesConflicts: ({
				appId,
				changeId,
			}: {
				appId: AppId;
				changeId: ChangeId;
			}) => (
				{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType, createAnalyticsEvent }: StoreContainerProps,
			) => void;
			collectScreenTabsAppliedChangesHistory: ({
				appId,
				changeId,
				screenTabs,
			}: {
				appId: AppId;
				changeId: ChangeId;
				screenTabs: ScreenTabsDataPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			collectFieldsAppliedChangesHistory: ({
				appId,
				changeId,
				fieldsChanges,
			}: {
				appId: AppId;
				changeId: ChangeId;
				fieldsChanges: FieldChangesMapPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNotification: (
				notificationProperties: NotificationState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		}
	>
> = createActionsHook(store);

export const useAdjustedScreenTabs: HookFunction<
	ScreenTabsDataPublic | null,
	BoundActions<
		IssueAdjustmentsState,
		{
			triggerFieldInitialization: () => ({
				getState,
				setState,
			}: StoreActionApi<IssueAdjustmentsState>) => void;
			processChange: ({
				fieldId,
				fieldValue,
				eventType,
			}: Omit<ProcessChangePayload, 'fieldType'>) => (
				{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			setFieldValue: ({
				fieldId,
				fieldType,
				fieldValue,
			}: {
				fieldId: FieldId;
				fieldType: FieldType;
				fieldValue: FieldInternalValue;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeField: ({
				field,
				metadata,
			}: InitializeFieldPayload) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeFields: (
				fields: InitializeFieldPayload[],
			) => (
				{ setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			startFieldsInitialisationTimeout: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			successFieldsInitalisation: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			registerFields: ({
				changeId,
				fieldsToBeRegistered,
				appId,
			}: {
				changeId: ChangeId;
				fieldsToBeRegistered: RegisteredFields;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			unregisterFields: ({
				changeId,
				appId,
			}: {
				changeId: ChangeId;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateFields: ({
				changeId,
				fieldsChanges,
				lookupDataFromApi,
				tenantContext,
				executionContext,
				environment,
				appId,
				intl,
				createAnalyticsEvent,
			}: {
				changeId: ChangeId;
				fieldsChanges: FieldMapFromIframe;
				lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
				tenantContext: TenantContext;
				executionContext: ExecutionContext;
				environment: IEnvironment;
				appId: AppId;
				intl: IntlShape;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
			clearAppliedValue: (
				fieldId: FieldId,
			) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setError: (
				errorProperties: ErrorState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNumberOfSupportedFields: (
				numberOfAllSupportedFields: number,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			/**
			 * This is used to block onInit for specific case like Issue View background refresh loading.
			 */
			setExperienceDataIsLoading: (
				experienceDataIsLoading: boolean,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setAppErrors: ({
				errors,
				appId,
			}: {
				errors: AppError[];
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAppErrors: ({
				appId,
			}: {
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			addAppsErrors: ({
				errors,
			}: {
				errors: Record<AppId, AppError[]>;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeScreenTabs: ({
				tabs,
				selected,
			}: {
				tabs: Readonly<
					Array<{
						id: string;
					}>
				>;
				selected?: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateScreenTabs: ({
				screenTabsChanges,
				changeId,
				appId,
			}: {
				screenTabsChanges: ScreenTabsChangesPublic;
				appId: AppId;
				changeId: ChangeId;
			}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateActiveScreenTab: ({
				selected,
			}: {
				selected: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			detectAppliedChangesConflicts: ({
				appId,
				changeId,
			}: {
				appId: AppId;
				changeId: ChangeId;
			}) => (
				{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType, createAnalyticsEvent }: StoreContainerProps,
			) => void;
			collectScreenTabsAppliedChangesHistory: ({
				appId,
				changeId,
				screenTabs,
			}: {
				appId: AppId;
				changeId: ChangeId;
				screenTabs: ScreenTabsDataPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			collectFieldsAppliedChangesHistory: ({
				appId,
				changeId,
				fieldsChanges,
			}: {
				appId: AppId;
				changeId: ChangeId;
				fieldsChanges: FieldChangesMapPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNotification: (
				notificationProperties: NotificationState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		}
	>,
	void
> = createHook(store, {
	selector: (state: IssueAdjustmentsState) => state.screenTabs,
});
